/**
 * Cliente del endpoint `GET /version` — Sprint 16 S16-08.
 *
 * El endpoint es público (sin auth) por D-S16-4. Devuelve las 4
 * versiones del proyecto (Portal / Administración / Tesorería /
 * Comisión) en formato `YYYY.MM.DD.NNNN / {tag} - {sha[:7]}`.
 *
 * Cache stale-while-revalidate en localStorage con TTL 5 min. El
 * footer del portal lo consume sin bloquear el render — si hay cache
 * válido, se muestra inmediatamente; si está expirado, se muestra el
 * stale y se actualiza en background.
 */
import { vistaActualPorPath } from '../layout/vista';

const baseUrl = import.meta.env.VITE_API_BASE_URL;

if (!baseUrl) {
  throw new Error('VITE_API_BASE_URL es obligatoria. Revisa .env.');
}

const CACHE_KEY = 'calbucura_version_cache_v1';
const TTL_MS = 5 * 60 * 1000; // 5 minutos

export interface VersionResponse {
  portal: string | null;
  administracion: string | null;
  tesoreria: string | null;
  comision: string | null;
  consultado_at: string;
}

export type ModuloFuncional =
  | 'portal'
  | 'administracion'
  | 'tesoreria'
  | 'comision';

interface CacheEntry {
  data: VersionResponse;
  /** Epoch ms cuando se guardó la entrada. */
  guardado_at: number;
}

function leerCache(): CacheEntry | null {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as CacheEntry;
  } catch {
    return null;
  }
}

function guardarCache(data: VersionResponse): void {
  try {
    const entry: CacheEntry = { data, guardado_at: Date.now() };
    localStorage.setItem(CACHE_KEY, JSON.stringify(entry));
  } catch {
    // localStorage puede estar lleno o deshabilitado; silenciamos —
    // la falta de cache solo significa que cada llamada hace fetch.
  }
}

async function fetchVersion(): Promise<VersionResponse> {
  const res = await fetch(`${baseUrl}/version`, {
    headers: { Accept: 'application/json' },
  });
  if (!res.ok) {
    throw new Error(`GET /version respondió ${res.status}`);
  }
  return (await res.json()) as VersionResponse;
}

/**
 * Retorna la versión cacheada (si existe) y dispara un refresh en
 * background si el cache está expirado o ausente. Si NO hay cache,
 * espera el fetch antes de retornar.
 *
 * Patrón stale-while-revalidate: el caller obtiene respuesta
 * inmediata cuando hay cache (aunque sea viejo), y la próxima vez
 * verá los datos actualizados.
 */
export async function getVersion(): Promise<VersionResponse> {
  const cached = leerCache();
  const ahora = Date.now();

  if (cached && ahora - cached.guardado_at < TTL_MS) {
    // Cache fresco: devuelve sin tocar la red.
    return cached.data;
  }

  if (cached) {
    // Cache viejo: dispara refresh background, devuelve stale.
    fetchVersion()
      .then(guardarCache)
      .catch(() => {
        /* silencioso — siguiente call reintentará */
      });
    return cached.data;
  }

  // Sin cache: fetch sincrónico.
  const fresco = await fetchVersion();
  guardarCache(fresco);
  return fresco;
}

/**
 * Detecta el módulo activo según el pathname del browser.
 *
 * Delega en `vistaActualPorPath` (lib/layout/vista) y mapea la vista a la
 * clave de versión del backend:
 * - `/tesoreria/*` → Tesorería.
 * - `/admin/*` (y el alias futuro `/comision/*`) → Comisión.
 * - Cualquier otro path en portal.calbucura.cl → Portal (vista del
 *   vecino: dashboard, mis datos, mi cuenta, documentos, avisos).
 *
 * Para `frontend-superadmin` (`sa.calbucura.cl`) se usa
 * `'administracion'` fijo — esa app no llama esta función.
 */
export function detectarModuloActivo(pathname: string): ModuloFuncional {
  // Reusa el helper canónico de vista (lib/layout/vista) y mapea la vista
  // 'vecino' a la clave 'portal' que espera el backend de versiones.
  const vista = vistaActualPorPath(pathname);
  return vista === 'vecino' ? 'portal' : vista;
}

/**
 * Extrae el `version_string` del módulo dado de la respuesta. Retorna
 * `null` si ese módulo no tiene historia de deploys registrada.
 */
export function versionDeModulo(
  response: VersionResponse,
  modulo: ModuloFuncional,
): string | null {
  return response[modulo];
}
